import { useState, useEffect, useCallback } from "react";
import VitalsService from "../services/vitals.service";
import { SterilizationStatus, Vital, VitalDto } from "../types/vitals";
import { cmToFeetInches, feetInchesToCm } from "../utils/heightConversion";

export function useVitals(memberId: string | undefined) {
  // Local state for form fields
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [heightUnit, setHeightUnit] = useState<"cm" | "ft/in">("ft/in");
  const [weight, setWeight] = useState("");
  const [init, setInit] = useState(false);
  const [sterilizationStatus, setSterilizationStatus] = useState<
    SterilizationStatus | ""
  >("");
  const [oSterilizationStatus, setOSterilizationStatus] = useState<
    SterilizationStatus | ""
  >("");
  const [oHeight, setOHeight] = useState("");
  const [oHeightIn, setOHeightIn] = useState("");
  const [oHeightCm, setOHeightCm] = useState("");
  const [oHeightUnit, setOHeightUnit] = useState<"cm" | "ft/in">("ft/in");
  const [oWeight, setOWeight] = useState("");

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isCreateError, setIsCreateError] = useState(false);
  const [isCreateSuccess, setIsCreateSuccess] = useState(false);
  const [vitals, setVitals] = useState<Vital | null>(null);

  // Fetch vitals for a member
  const fetchVitals = useCallback(async () => {
    if (!memberId) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const data = await VitalsService.getVitals(memberId);
      setVitals(data);
    } catch (error) {
      console.error("Failed to fetch vitals:", error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [memberId]);

  // Create or update vitals for a member
  const createVitals = useCallback(
    async (dto: VitalDto) => {
      if (!memberId) throw new Error("No memberId");

      setIsCreateError(false);
      setIsCreateSuccess(false);

      try {
        const result = await VitalsService.createVitals(memberId, dto);
        setVitals(result);
        setIsCreateSuccess(true);
        // Refetch to get updated data
        await fetchVitals();
        return result;
      } catch (error) {
        console.error("Failed to create vitals:", error);
        setIsCreateError(true);
        throw error;
      }
    },
    [memberId, fetchVitals]
  );

  // Populate form with vitals on load
  useEffect(() => {
    const vitalsArr =
      vitals &&
      typeof vitals === "object" &&
      "vitals" in vitals &&
      Array.isArray((vitals as Vital).vitals)
        ? (vitals as Vital).vitals
        : undefined;
    if (vitalsArr && !init) {
      let ft = "",
        inch = "",
        cm = "",
        unit: "cm" | "ft/in" = "ft/in",
        wt = "",
        sterilizationStat: SterilizationStatus | "" = "";
      vitalsArr.forEach((v: any) => {
        if (v.name === "height") {
          if (v.unit === "cm") {
            cm = v.value;
            unit = "cm";
          } else {
            const match = v.value.match(/^(\d+)'?(\d*)$/);
            if (match) {
              ft = match[1] || "";
              inch = match[2] || "";
            }
            unit = "ft/in";
          }
        }
        if (v.name === "weight") {
          wt = v.value;
        }
        if (v.name === "sterilization") {
          const status = v.value as SterilizationStatus;
          sterilizationStat = status;
        }
      });
      setHeightFt(ft);
      setHeightIn(inch);
      setHeightCm(cm);
      setHeightUnit(unit);
      setWeight(wt);
      setSterilizationStatus(sterilizationStat);
      setOHeight(ft);
      setOHeightIn(inch);
      setOHeightCm(cm);
      setOHeightUnit(unit);
      setOWeight(wt);
      setOSterilizationStatus(sterilizationStat);
      setInit(true);
    }
  }, [vitals, init]);

  // Add a refetch counter to trigger refetch when needed
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Initial fetch and refetch when counter changes
  useEffect(() => {
    fetchVitals();
  }, [fetchVitals, refetchCounter]);

  // Function to manually trigger refetch
  const triggerRefetch = useCallback(() => {
    setRefetchCounter((prev) => prev + 1);
  }, []);

  const isChanged = function (): boolean {
    return (
      heightFt !== oHeight ||
      heightIn !== oHeightIn ||
      heightCm !== oHeightCm ||
      heightUnit !== oHeightUnit ||
      weight !== oWeight ||
      sterilizationStatus !== oSterilizationStatus
    );
  };

  // Handle unit toggle with conversion
  const handleUnitToggle = (useCm: boolean) => {
    const newUnit = useCm ? "cm" : "ft/in";

    if (newUnit === heightUnit) return;

    if (newUnit === "cm") {
      // Convert ft/in to cm
      if (heightFt && heightIn !== undefined) {
        const cm = feetInchesToCm(Number(heightFt), Number(heightIn || 0));
        setHeightCm(cm.toString());
      }
    } else {
      // Convert cm to ft/in
      if (heightCm) {
        const { feet, inches } = cmToFeetInches(Number(heightCm));
        setHeightFt(feet.toString());
        setHeightIn(inches.toString());
      }
    }

    setHeightUnit(newUnit);
  };

  return {
    vitals,
    isLoading,
    isError,
    refetch: fetchVitals,
    triggerRefetch,
    createVitals,
    isCreateError,
    isCreateSuccess,
    isChanged,
    heightFt,
    setHeightFt,
    heightIn,
    setHeightIn,
    heightCm,
    setHeightCm,
    heightUnit,
    setHeightUnit,
    handleUnitToggle,
    weight,
    setWeight,
    sterilizationStatus,
    setSterilizationStatus,
    oHeight,
    oHeightIn,
    oHeightCm,
    oHeightUnit,
    oWeight,
    oSterilizationStatus,
    init,
    setInit,
  };
}
